// dependencies
import React, { useEffect, useMemo, useRef, useState } from "react";

import { PortalContext } from "../../contexts";

export interface UsePortalOptions {
	/**
	 * Whether the portal should be rendered and body scroll locked
	 */
	shouldRender: boolean;
	/**
	 * Custom data attribute for the portal container (for testing/debugging)
	 * @default "data-portal-container"
	 */
	portalAttribute?: string;
	/**
	 * Whether to prevent body scrolling when portal is active
	 * @default true
	 */
	preventBodyScroll?: boolean;
	/**
	 * Container element to append the portal to. Pass null to disable portal creation.
	 * @default document.body
	 */
	container?: HTMLElement | null;
}

export interface UsePortalReturn {
	/**
	 * Portal container element reference
	 */
	portalRef: React.RefObject<HTMLDivElement | null>;
	/**
	 * Whether the portal is currently rendered in the DOM
	 */
	isRendered: boolean;
	/**
	 * Portal context provider component for enabling automatic portal detection.
	 * Use this to wrap content that should be aware of the portal container.
	 */
	PortalProvider: React.ComponentType<{ children: React.ReactNode }>;
}

/**
 * Hook for managing portal rendering with body scroll prevention.
 *
 * This hook handles the creation, mounting, and cleanup of portal containers
 * along with optional body scroll locking. It's designed for components like
 * modals, drawers, and overlays that need to render outside the normal DOM hierarchy.
 *
 * ## Features
 * - **Portal container management**: Creates and manages portal DOM elements
 * - **Body scroll prevention**: Optionally locks body scroll when portal is active using position: fixed
 * - **Scroll position preservation**: Maintains scroll position when locking/unlocking
 * - **Custom containers**: Support for custom portal containers
 * - **Cleanup handling**: Automatic cleanup on unmount and state changes
 * - **Testing support**: Custom data attributes for easier testing
 *
 * ## Usage
 * ```tsx
 * const Component = ({ isOpen, children }) => {
 *   const { portalRef, isRendered } = usePortal({
 *     shouldRender: isOpen,
 *     portalAttribute: "data-my-modal-portal"
 *   });
 *
 *   if (!isRendered || !portalRef.current) {
 *     return null;
 *   }
 *
 *   return createPortal(
 *     <div>{children}</div>,
 *     portalRef.current
 *   );
 * };
 * ```
 *
 * @param options - Configuration options for the portal
 * @returns Portal reference and rendering state
 */
export const usePortal = ({
	shouldRender,
	portalAttribute = "data-portal-container",
	preventBodyScroll = true,
	container,
}: UsePortalOptions): UsePortalReturn => {
	// Default to document.body when container is not provided, but respect explicit null
	const effectiveContainer =
		container === null ? null : (container ?? (typeof document !== "undefined" ? document.body : undefined));
	const portalRef = useRef<HTMLDivElement | null>(null);
	const [isRendered, setIsRendered] = useState(false);

	// Manage portal container mounting and body scroll lock with a single cleanup
	useEffect(() => {
		// If no container or not rendering, ensure state reflects not rendered
		if (!effectiveContainer || !shouldRender) {
			setIsRendered(false);
			return;
		}

		// Ensure portal element exists
		if (!portalRef.current) {
			portalRef.current = document.createElement("div");
			portalRef.current.setAttribute(portalAttribute, "true");
		}

		const portalElement = portalRef.current;
		effectiveContainer.appendChild(portalElement);
		setIsRendered(true);

		const previousBodyPosition = document.body.style.position;
		const previousBodyTop = document.body.style.top;
		const previousBodyLeft = document.body.style.left;
		const previousBodyRight = document.body.style.right;
		const previousBodyWidth = document.body.style.width;
		const scrollY = window.scrollY;

		if (preventBodyScroll) {
			document.body.style.position = "fixed";
			document.body.style.top = `-${scrollY}px`;
			document.body.style.left = "0";
			document.body.style.right = "0";
			document.body.style.width = "100%";
		}

		return () => {
			if (portalElement && effectiveContainer.contains(portalElement)) {
				effectiveContainer.removeChild(portalElement);
			}
			setIsRendered(false);
			if (preventBodyScroll) {
				document.body.style.position = previousBodyPosition;
				document.body.style.top = previousBodyTop;
				document.body.style.left = previousBodyLeft;
				document.body.style.right = previousBodyRight;
				document.body.style.width = previousBodyWidth;
				window.scrollTo(0, scrollY);
			}
		};
	}, [effectiveContainer, portalAttribute, preventBodyScroll, shouldRender]);

	const PortalProvider = useMemo(() => {
		const Component = ({ children }: { children: React.ReactNode }) => {
			const contextValue = {
				container: portalRef.current,
				depth: 0,
			};

			return <PortalContext.Provider value={contextValue}>{children}</PortalContext.Provider>;
		};
		Component.displayName = "PortalProvider";
		return Component;
	}, []);

	return {
		portalRef,
		isRendered,
		PortalProvider,
	};
};

usePortal.displayName = "usePortal";
